import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { sortBy } from 'lodash';
import { Delete } from 'jbc-front/components/icons';
import ActionButton from 'jbc-front/components/ActionButton';
import Button from 'jbc-front/components/Button';
import { Modal } from 'jbc-front/components/presenters/ui/Modal';
import { Content } from '@/components/layout/Content';
import { LoadingPage } from '@/components/LoadingPage';
import { Table } from '@/components/Table';
import { NotFound } from '@/components/NotFound';
import { dateFormat } from '@/components/utils/formatter';
import { useQuery } from '@/hooks/graphql/useQuery';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useNotify } from '@/hooks/useNotify';
import {
  PERSONAL_RESULTS,
  PersonalResultsQueryResult,
  PersonalResultsQueryVariables,
  DELETE_REGULAR_HEALTH_CHECKUP_RESULT,
  DeleteRegularHealthCheckupResultQueryResult,
  DeleteRegularHealthCheckupResultQueryVariables,
  PartialRegularHealthCheckupResult
} from './query';
import styles from './DeletePersonalResults.scss';

interface SearchParams {
  personalId: string;
}

export const DeletePersonalResults: FC = () => {
  const notify = useNotify();
  const { personalId } = useParams<SearchParams>();
  const { data, loading, refetch } = useQuery<PersonalResultsQueryResult, PersonalResultsQueryVariables>(
    PERSONAL_RESULTS,
    {
      variables: { personalId },
      fetchPolicy: 'network-only'
    }
  );
  const results = data?.client?.employee?.regularHealthCheckupResults;
  const sortedResults = sortBy(results, result => result.dateOn);

  const [deleteRegularHealthCheckupResult, { loading: deleting }] = useMutation<
    DeleteRegularHealthCheckupResultQueryResult,
    DeleteRegularHealthCheckupResultQueryVariables
  >(DELETE_REGULAR_HEALTH_CHECKUP_RESULT);

  const [selectedResult, setSelectedResult] = useState<PartialRegularHealthCheckupResult | null>(null);
  const handleCloseModal = () => setSelectedResult(null);

  const handleSubmit = async () => {
    if (!selectedResult) return;

    await deleteRegularHealthCheckupResult({
      variables: {
        input: {
          personalId,
          resultId: selectedResult.id
        }
      }
    });
    notify('削除しました', 'success');
    setSelectedResult(null);
    refetch();
  };

  if (!loading && !results) {
    return <NotFound />;
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">過去の健康診断結果の削除</h1>
      </div>
      <Content size="xs">
        {results ? (
          <Table className={styles.table} fixed>
            <thead>
              <tr>
                <th className={styles.dateOnColumn}>定期健康診断実施日</th>
                <th className={styles.deleteColumn}>削除</th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.length === 0 ? (
                <tr>
                  <td colSpan={2}>健康診断結果がありません</td>
                </tr>
              ) : (
                sortedResults.map(result => (
                  <tr key={result.id}>
                    <td className={styles.dateOnColumn}>{dateFormat(result.dateOn, 'seireki')}</td>
                    <td className={styles.deleteColumn}>
                      <ActionButton
                        className={styles.deleteButton}
                        icon={<Delete size={16} />}
                        onClick={() => setSelectedResult(result)}>
                        削除
                      </ActionButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        ) : (
          <LoadingPage />
        )}
        <Modal isOpen={!!selectedResult} onClose={handleCloseModal}>
          <Modal.Header onClose={handleCloseModal}>削除確認</Modal.Header>
          <Modal.Body>
            <p className={styles.warningText}>
              {dateFormat(selectedResult?.dateOn, 'seireki')}の定期健康診断の個人結果を削除します。
              <br />
              削除された個人結果を復元することはできません。
            </p>
            <p className="u-mt20">個人結果を削除しますか？</p>
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <Button className={styles.footerButton} onClick={handleCloseModal}>
              いいえ
            </Button>
            <Button className={styles.footerButton} primary onClick={handleSubmit} disabled={deleting}>
              はい
            </Button>
          </Modal.Footer>
        </Modal>
      </Content>
    </>
  );
};
